/** Decides demo vs live mode, follows the Supabase session, opens the user's local database and starts sync. */
import type { Session } from '@supabase/supabase-js';
import { hasBackend } from '@/lib/env';
import { supabase } from '@/lib/supabase';
import { openDb } from '@/db/dexie';
import { DEMO_USER_ID, seedDemo, seedMany } from '@/db/demo-seed';
import { useSession } from '@/state/session';
import { setTheme } from '@/lib/theme';
import { startSync, stopSync } from '@/sync/engine';

const DEMO_KEY = 'karya-demo';

export function wantsDemo(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.has('demo')) {
    try {
      localStorage.setItem(DEMO_KEY, params.get('demo') === '0' ? '0' : '1');
    } catch {
      /* ignore */
    }
  }
  try {
    return !hasBackend || localStorage.getItem(DEMO_KEY) === '1';
  } catch {
    return !hasBackend;
  }
}

export function leaveDemo(): void {
  try {
    localStorage.setItem(DEMO_KEY, '0');
  } catch {
    /* ignore */
  }
  window.location.assign('/');
}

export async function startDemo(): Promise<void> {
  const d = openDb('demo');
  await seedDemo(d);
  const many = Number(new URLSearchParams(window.location.search).get('seed') ?? 0);
  if (many > 0 && (await d.tasks.count()) < many) await seedMany(d, many);
  useSession
    .getState()
    .set({ mode: 'demo', userId: DEMO_USER_ID, email: 'demo@karya.app', ready: true });
}

let currentUser: string | null = null;

async function handleSession(session: Session | null): Promise<void> {
  const set = useSession.getState().set;
  if (!session) {
    currentUser = null;
    stopSync();
    set({ mode: 'signed-out', userId: null, email: null, ready: true });
    return;
  }
  if (currentUser === session.user.id) return;
  currentUser = session.user.id;
  const { data: allowed, error } = await supabase!.rpc('is_allowed_user');
  if (error || !allowed) {
    set({
      mode: 'not-allowed',
      userId: session.user.id,
      email: session.user.email ?? null,
      ready: true,
    });
    return;
  }
  const d = openDb(session.user.id);
  const hasLocal = (await d.spaces.count()) > 0 || (await d.profiles.count()) > 0;
  set({
    mode: 'live',
    userId: session.user.id,
    email: session.user.email ?? null,
    ready: hasLocal,
  });
  // With a local copy we show it instantly and sync in the background.
  const sync = startSync(session.user.id).then(async () => {
    const p = await d.profiles.get(session.user.id);
    if (p?.theme) setTheme(p.theme);
  });
  if (!hasLocal) await sync;
  set({ ready: true });
}

let booting: Promise<void> | null = null;

/** Idempotent: React strict mode calls effects twice in development. */
export function bootstrap(): Promise<void> {
  booting ??= doBootstrap();
  return booting;
}

async function doBootstrap(): Promise<void> {
  if (wantsDemo() || !supabase) {
    await startDemo();
    return;
  }
  const { data } = await supabase.auth.getSession();
  await handleSession(data.session);
  supabase.auth.onAuthStateChange((_event, session) => {
    // Defer: supabase-js warns against awaiting other calls inside this callback.
    setTimeout(() => void handleSession(session), 0);
  });
}

export async function signOut(): Promise<void> {
  stopSync();
  await supabase?.auth.signOut();
}
